import { useEffect, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import {
  StripeCheckoutSessionStatus,
  GetStripeCheckoutSessionStatusDocument,
} from '@sandtech-hub-monorepo/ehub-client';

import { AppRoutesPaths, subscribePath } from '../../routes/types';
import {
  SUCCESS_ICON,
  SubscribeMessage,
  WAITING_ICON,
} from '../../components/subscribe/SubscribeMessage';
import { useProgramCoverage } from '../../data/useProgramCoverage';
import { useEnrollInProgram } from '../../data/useEnrollInProgram';
import { programTitle, usePrograms } from '../../data/programs';

/**
 * Where the checkout page sends the learner once Stripe reports the session
 * finished. Stripe does not send them: the sessions are minted with
 * `redirect_on_completion: 'never'`, so both ids in the url are put there by
 * our own `onComplete` handler.
 *
 * Landing here proves nothing by itself — the url is bookmarkable, shareable
 * and typeable — so the page asks two separate questions instead of trusting
 * it. `stripeCheckoutSessionStatus` says what became of the session, and
 * throws for an id that is unknown or belongs to someone else.
 * `stripeProgramCoverage` says whether access actually landed, because the
 * subscription is mirrored locally by a webhook that races the learner's own
 * return.
 *
 * COMPLETE means the session finished, not that the first invoice was paid:
 * `payment_status` is deliberately not exposed. A declined first payment
 * leaves an INCOMPLETE subscription, which does not entitle, and from here
 * that is indistinguishable from a webhook still in flight. Both end at the
 * poll ceiling — which is why that state says access has not appeared yet and
 * never says the payment failed.
 */
export const SubscribeConfirmation = () => {
  const [params] = useSearchParams();
  const checkoutSessionId = params.get('checkoutSessionId');
  const programId = params.get('programId');
  /*
   * Set by the plan picker when the learner came here to start a particular
   * program. Absent for a cold purchase from the banner or Settings, where the
   * `programId` above was derived from the plan's coverage to satisfy the
   * checkout mutation and names nothing the learner chose.
   */
  const enrollAfterCheckout = params.get('enroll') === '1';

  /*
   * What Stripe says became of the session. Not a nullable read: the server
   * throws for an id it cannot find and for one belonging to somebody else,
   * with the same message for both, so session ids are not probeable — which
   * is why `statusError` is a real branch below rather than an edge case.
   *
   * Asked of Stripe rather than the cache: a session that was OPEN when this
   * page last rendered may have been paid in another tab since.
   */
  const {
    data: statusData,
    loading: statusQueryLoading,
    error: statusError,
  } = useQuery(GetStripeCheckoutSessionStatusDocument, {
    variables: { checkoutSessionId: checkoutSessionId ?? '' },
    skip: !checkoutSessionId,
    fetchPolicy: 'network-only',
  });

  const status = statusData?.stripeCheckoutSessionStatus.status ?? null;
  const statusLoading = !!checkoutSessionId && statusQueryLoading;

  /*
   * Enrol, now that Stripe has confirmed the session. This is the other end of
   * the plan picker: a paid plan enrols once the payment is real, exactly as
   * "Stay on Free" enrols immediately.
   *
   * It waits for COMPLETE rather than acting on the checkout page's own
   * `onComplete`, so the enrolment follows a session the server verified
   * belongs to this learner rather than a url they could have typed. The ref
   * fires it once: `status` settles before the coverage poll below starts
   * re-rendering, and enrolling twice would surface the server's
   * already-enrolled refusal as an error toast.
   *
   * `enroll` navigates to the learning page on success, which is where this
   * flow ends.
   */
  const { enroll, enrolling } = useEnrollInProgram();
  const enrollmentStarted = useRef(false);

  useEffect(() => {
    if (
      !enrollAfterCheckout ||
      !programId ||
      status !== StripeCheckoutSessionStatus.Complete ||
      enrollmentStarted.current
    ) {
      return;
    }

    enrollmentStarted.current = true;
    enroll(programId);
  }, [enrollAfterCheckout, programId, status, enroll]);

  const {
    covered,
    timedOut,
    error: coverageError,
  } = useProgramCoverage(programId, {
    // Only once the session is finished. Polling an abandoned checkout would
    // wait for something that is never coming.
    poll: status === StripeCheckoutSessionStatus.Complete,
  });

  // For the title and the way onward only, so nothing waits on it: a
  // confirmation that cannot name the program still confirms the payment, and
  // falls back to the learning page below.
  const { available } = usePrograms('gsa');

  const program = useMemo(
    () => available.find((candidate) => candidate.id === programId),
    [available, programId]
  );

  const plansLink = subscribePath(programId);

  const learningLink = (
    <Link to={AppRoutesPaths.Learning}>Go to my learning</Link>
  );

  if (!checkoutSessionId) {
    return (
      <SubscribeMessage title="Nothing to confirm">
        This page is the landing point for a completed checkout, and no checkout
        session was supplied.
      </SubscribeMessage>
    );
  }

  // The server says the same thing for an id that does not exist and one that
  // belongs to somebody else, on purpose, so that session ids cannot be probed.
  if (statusError) {
    return (
      <SubscribeMessage title="We couldn't check that checkout">
        {statusError.message}
      </SubscribeMessage>
    );
  }

  if (statusLoading || !status) {
    return (
      <SubscribeMessage title="Checking your payment" icon={WAITING_ICON}>
        One moment while we ask Stripe how this checkout ended.
      </SubscribeMessage>
    );
  }

  if (status === StripeCheckoutSessionStatus.Expired) {
    return (
      <SubscribeMessage
        title="That checkout expired"
        action={<Link to={plansLink}>Back to plans</Link>}
      >
        Checkout sessions don&apos;t stay open indefinitely. Nothing was charged
        — choose your plan again and we&apos;ll start a fresh one.
      </SubscribeMessage>
    );
  }

  // OPEN is a session nobody submitted: the window was closed, or the card was
  // refused before it could be taken. Either way there is no payment, so this
  // must not congratulate anyone.
  if (status === StripeCheckoutSessionStatus.Open) {
    return (
      <SubscribeMessage
        title="This checkout wasn't completed"
        action={<Link to={plansLink}>Back to plans</Link>}
      >
        Nothing has been charged. You can pick your plan again and we&apos;ll
        take you back to payment.
      </SubscribeMessage>
    );
  }

  // COMPLETE from here down.

  /*
   * Enrolment is running, and it navigates to the learning page when it lands,
   * so this is the last thing the learner sees on a scoped purchase. Ahead of
   * the coverage branches below deliberately: those describe a subscription
   * the learner is about to be taken away from, and "You're all set" followed
   * by an immediate redirect reads as a glitch.
   */
  if (enrollAfterCheckout && programId) {
    return (
      <SubscribeMessage title="Payment received" icon={WAITING_ICON}>
        {enrolling
          ? 'Adding the program to your learning…'
          : 'One moment while we open your program.'}
      </SubscribeMessage>
    );
  }

  if (!programId) {
    return (
      <SubscribeMessage
        title="Payment received"
        icon={SUCCESS_ICON}
        action={learningLink}
      >
        Stripe has finished this checkout. This link doesn&apos;t say which
        program it was for, so we can&apos;t confirm what it unlocked from here.
      </SubscribeMessage>
    );
  }

  if (covered) {
    return (
      <SubscribeMessage
        title="You're all set"
        icon={SUCCESS_ICON}
        action={
          program ? (
            <Link
              to={AppRoutesPaths.Program.replace(
                ':programId',
                program.abbreviation
              )}
            >
              Start {programTitle(program)}
            </Link>
          ) : (
            learningLink
          )
        }
      >
        Your subscription is active
        {program ? ` and ${programTitle(program)} is unlocked` : ''}. It also
        unlocks every other program the plan covers.
      </SubscribeMessage>
    );
  }

  // The ceiling, not a verdict. Access may still appear a moment from now, and
  // nothing readable from here can tell that apart from a first payment that
  // was declined.
  if (timedOut || coverageError) {
    return (
      <SubscribeMessage
        title="Still setting up your access"
        action={learningLink}
      >
        Stripe has finished the checkout, but access hasn&apos;t appeared on
        your account yet. This usually clears within a minute — refresh this
        page to check again. If your card was declined, Stripe will have emailed
        you.
      </SubscribeMessage>
    );
  }

  return (
    <SubscribeMessage
      title="Setting up your access"
      icon={WAITING_ICON}
      action={learningLink}
    >
      Stripe has your payment. We&apos;re waiting for the confirmation to land,
      which usually takes a few seconds.
    </SubscribeMessage>
  );
};
